import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { Component, OnInit, Inject, ViewChild, ElementRef } from '@angular/core';

import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormControl, FormBuilder, Validators, AbstractControl } from '@angular/forms';

import { GoogleMap } from '@angular/google-maps';
import * as moment from 'moment-timezone';
import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';

import { SpinnerService } from '../../services/spinner.service';
import { ApiHall, ApiSetHallsRequest, SettingFile } from '../../api-client-module';
import { UploadedFile } from '../../firestore-models/uploaded-file';
import { ProjectLanguage } from '../../firestore-models/project-language';

import { ConfigurationService } from '../../configuration.service';
import { ApiClientService } from '../../api-client.service';
import { AppConst } from '../../app-const';
import { MultiNameDialogComponent } from '../../common/multi-name-dialog/multi-name-dialog.component';
import { MultiDescDialogComponent } from '../../common/multi-desc-dialog/multi-desc-dialog.component';
import { SelectFaviconFileDialogComponent } from '../../select-favicon-file-dialog/select-favicon-file-dialog.component';
import { SelectAngularIconDialogComponent } from '../../select-angular-icon-dialog/select-angular-icon-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';

export interface HallDialogData {
  change_type: number;
  hall?: ApiHall;
}

@Component({
  selector: 'app-hall-dialog',
  templateUrl: './hall-dialog.component.html',
  styleUrls: ['./hall-dialog.component.css'],
  standalone: false,
  providers: [
    // このコンポーネントとその子要素専用のDateAdapterインスタンスを作成
    { provide: DateAdapter, useClass: NativeDateAdapter },
  ]
})
export class HallDialogComponent implements OnInit {

  //建物情報
  hall?: ApiHall;
  isAdded: boolean = false;
  projectLanguages!: Array<ProjectLanguage>;
  //各建物情報に紐付くプロジェクトID
  projectId: string;

  //建物情報フォーム
  hallFormGroup!: FormGroup;
  hallIdControl!: FormControl;
  hallExplainControl!: FormControl;
  hallNameControl!: FormControl;
  hallLatControl!: FormControl;
  hallLngControl!: FormControl;
  showMarkerControl!: FormControl;
  menuDispTypeControl!: FormControl;
  menuDispMapRangeControl!: FormControl;
  showPaidContentControl!: FormControl;
  hallDescsControl!: FormControl;
  salesStartDateControl!: FormControl;
  salesEndDateControl!: FormControl;
  priceControl!: FormControl;
  validityPeriodControl!: FormControl;
  useCustomMenuControl!: FormControl;
  useCustomMenuIconControl!: FormControl;
  useCustomSideIconControl!: FormControl;
  useCustomSideImageControl!: FormControl;

  menuDispTypes = AppConst.MENU_DISP_TYPES;
  menuDispMapRanges = AppConst.MENU_DISP_MAP_RANGES;
  readonly MENU_DISP_TYPE_MENU: number = AppConst.MENU_DISP_TYPE_MENU;

  useFunctionNotUsed = AppConst.USE_FUNCTION_NOT_USED;
  useFunctions = AppConst.USE_FUNCTIONS;

  //GoogleMap表示
  zoom = AppConst.ZOOM_INITIAL_VALUE;
  center!: google.maps.LatLngLiteral;
  options: google.maps.MapOptions = {
    disableDefaultUI: true
  };
  currentPosition!: google.maps.LatLngLiteral
  currentPositionMarkerOption: google.maps.MarkerOptions = {
    draggable: true
  };

  projectBaseLang: string = 'ja';

  @ViewChild(GoogleMap, { static: false }) map!: GoogleMap;
  @ViewChild("mapSearchField") searchField!: ElementRef;
  @ViewChild("mapSearchFieldExecute") searchFieldExecute!: ElementRef;
  @ViewChild("mapSearchFieldClear") searchFieldClear!: ElementRef;
  searchBox!: google.maps.places.SearchBox;

  files: SettingFile[] = [];

  uploadedFilesCollectionName = AppConst.FIRESTORE_UPLOADED_FILES;
  customMenuIconFile!: UploadedFile;
  customSideIconFile!: UploadedFile;
  customSideImageFile?: UploadedFile;

  currentLang: string = 'ja';
  private langChangeSub!: Subscription; // TranslateServiceの購読を管理

  constructor(
    public dialogRef: MatDialogRef<HallDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: HallDialogData,
    private formBuilder: FormBuilder,
    public dialog: MatDialog,
    private configurationService: ConfigurationService,
    private angularFirestore: AngularFirestore,
    private translate: TranslateService,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private dateAdapter: DateAdapter<any>
  ) {
    this.projectId = this.configurationService.getProjectId();
    moment.tz.setDefault('Asia/Tokyo');    
  }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;

    this.currentLang = this.translate.currentLang; //(0703)
    // 1. コンポーネント初期化時に現在の言語でカレンダーを設定
    this.setCalendarLocale(this.currentLang);

    // 2. アプリケーションの言語が変更されたら、カレンダーの言語も動的に追従させる
    this.langChangeSub = this.translate.onLangChange.subscribe(event => {
      this.setCalendarLocale(event.lang);
    });

    this.hall = this.data.hall;
    if (!this.hall) {
      this.hall = {
        hall_id: '',
        hall_explain: '',
        hall_names: [],
        latitude: this.configurationService.getProject().project_geopoint && this.configurationService.getProject().project_geopoint.latitude ? this.configurationService.getProject().project_geopoint.latitude : AppConst.LATITUDE_INITIAL_VALUE,
        longitude: this.configurationService.getProject().project_geopoint && this.configurationService.getProject().project_geopoint.longitude ? this.configurationService.getProject().project_geopoint.longitude : AppConst.LONGITUDE_INITIAL_VALUE,
        show_marker: false,
        project_id: this.projectId,
        menu_disp_type: AppConst.MENU_DISP_TYPE_GUIDE,
        menu_disp_map_range: AppConst.MENU_DISP_MAP_RANGE_NONE,
        show_paid_content: false,
        hall_descs: null as any,
        sales_start_date: null as any,
        sales_end_date: null as any,
        price: 0,
        validity_period: 1,
        use_custom_menu: AppConst.USE_FUNCTION_PC_SP,
        use_custom_menu_icon: false,
        use_custom_side_icon: false,
        use_custom_side_image: false,
        custom_menu_icon_file: '',
        custom_side_icon_file: '',
        custom_side_image_file: '',
        menu_icon: '',
        side_icon: '',
        enabled: true,
      };
      this.isAdded = true;
    }
    this.currentPosition = {
      lat: this.hall.latitude!,
      lng: this.hall.longitude!
    }
    this.center = this.currentPosition;
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;

    this.createForm();
    this.hallIdControl = this.hallFormGroup.get('hallId') as FormControl;
    this.hallExplainControl = this.hallFormGroup.get('hallExplain') as FormControl;
    this.hallNameControl = this.hallFormGroup.get('hallName') as FormControl;
    this.hallLatControl = this.hallFormGroup.get('hallLat') as FormControl;
    this.hallLngControl = this.hallFormGroup.get('hallLng') as FormControl;
    this.showMarkerControl = this.hallFormGroup.get('showMarker') as FormControl;
    this.menuDispTypeControl = this.hallFormGroup.get('menuDispType') as FormControl;
    this.menuDispMapRangeControl = this.hallFormGroup.get('menuDispMapRange') as FormControl;
    this.showPaidContentControl = this.hallFormGroup.get('showPaidContent') as FormControl;
    this.hallDescsControl = this.hallFormGroup.get('hallDescs') as FormControl;
    this.salesStartDateControl = this.hallFormGroup.get('salesStartDate') as FormControl;
    this.salesEndDateControl = this.hallFormGroup.get('salesEndDate') as FormControl;
    this.priceControl = this.hallFormGroup.get('price') as FormControl;
    this.validityPeriodControl = this.hallFormGroup.get('validityPeriod') as FormControl;
    this.useCustomMenuControl = this.hallFormGroup.get('useCustomMenu') as FormControl;
    this.useCustomMenuIconControl = this.hallFormGroup.get('useCustomMenuIcon') as FormControl;
    this.useCustomSideIconControl = this.hallFormGroup.get('useCustomSideIcon') as FormControl;
    this.useCustomSideImageControl = this.hallFormGroup.get('useCustomSideImage') as FormControl;

    this.changeShowPaidContent();
    this.getCustomMenuIconFile();
    this.getCustomSideIconFile();
    this.getCustomSideImageFile();
  }

  ngAfterViewInit(): void {
    // 住所検索用
    this.searchBox = new google.maps.places.SearchBox(
      this.searchField.nativeElement
    );
    this.map.controls[google.maps.ControlPosition.TOP_CENTER].push(
      this.searchField.nativeElement
    );
    this.map.controls[google.maps.ControlPosition.TOP_CENTER].push(
      this.searchFieldExecute.nativeElement
    );
    this.map.controls[google.maps.ControlPosition.TOP_CENTER].push(
      this.searchFieldClear.nativeElement
    );
  }

  /** //(0703)
  * Datepickerのロケールを設定するプライベートメソッド
  * @param lang 設定したい言語コード (例: 'en', 'ja')
  */
  private setCalendarLocale(lang: string): void {
    // 言語に応じたロケールを設定
    const locale = lang === 'en' ? 'en-US' : 'ja-JP';
    this.dateAdapter.setLocale(locale);
  }
  
  //GoogleMap上のピンとフォームの値を一致させる
  onMerkerDragEnd(event: any) {
    this.hallLatControl.setValue(this.round(event.latLng!.lat(), 8));
    this.hallLngControl.setValue(this.round(event.latLng!.lng(), 8));
  }

  //ピンの位置
  private round(value: number, digits: number): number {
    let pow: number = 1;
    if (digits > 0) {
      pow = Math.pow(10, digits);
    }
    return Math.round(value * pow) / pow;
  }

  //Googleマップと緯度のフォーム連携
  onBlurMarkerLat() {
    if (!!this.hallLatControl.valid) {
      this.hallLatControl.setValue(this.round(this.hallLatControl.value, 8));
      this.currentPosition = {
        lat: this.round(this.hallLatControl.value, 8),
        lng: this.currentPosition.lng
      }
      this.map.panTo(this.currentPosition);
    }
  }

  //Googleマップと経度のフォーム連携
  onBlurMarkerLng() {
    if (!!this.hallLngControl.valid) {
      this.hallLngControl.setValue(this.round(this.hallLngControl.value, 8));
      this.currentPosition = {
        lat: this.currentPosition.lat,
        lng: this.round(this.hallLngControl.value, 8)
      }
      this.map.panTo(this.currentPosition);
    }
  }

  //以下、フォームの記入エラー表示
  public getErrorMessageToHallId() {
    return this.hallIdControl.hasError('required')
      ? this.translate.instant('必須項目')
      : this.hallIdControl.hasError('pattern')
          ? this.translate.instant('半角数字')
          : '';
  }

  public getErrorMessageToHallName() {
    return this.hallNameControl.hasError('required')
      ? this.translate.instant('必須項目')
      : '';
  }

  public getErrorMessageToHallLat() {
    return this.hallLatControl.hasError('required')
      ? this.translate.instant('必須項目')
      : this.hallLatControl.hasError('pattern') || this.hallLatControl.hasError('min') || this.hallLatControl.hasError('max')
          ? this.translate.instant('-90から90の間で小数第８位まで')
          : '';
  }

  public getErrorMessageToHallLng() {
    return this.hallLngControl.hasError('required')
      ? this.translate.instant('必須項目')
      : this.hallLngControl.hasError('pattern') || this.hallLngControl.hasError('min') || this.hallLngControl.hasError('max')
          ? this.translate.instant('-180から180の間で小数第８位まで')
          : '';
  }

  public getErrorMessageToMenuDispType() {
    return this.menuDispTypeControl.hasError('required')
      ? this.translate.instant('必須項目')
      : '';
  }

  public getErrorMessageToMenuDispMapRange() {
    return this.menuDispMapRangeControl.hasError('required')
      ? this.translate.instant('必須項目')
      : '';
  }

  public getErrorMessageToUseCustomMenu() {
    return this.useCustomMenuControl.hasError('required')
      ? this.translate.instant('必須項目')
      : '';
  }

  public getErrorMessageToHallDescs() {
    return this.hallDescsControl.hasError('required')
    ? this.translate.instant('必須項目')
    : '';
  }

  public getErrorMessageToPrice() {
    return this.priceControl.hasError('required')
    ? this.translate.instant('必須項目')
    : '';
  }

  public getErrorMessageToSalesEndDate() {
    return this.translate.instant('販売開始日より前の日は設定不可');
  }

  //フォームの値を各要素に代入
  private createForm() {
    this.hallFormGroup = this.formBuilder.group(
      {
        hallId: [this.hall!.hall_id, [Validators.required, Validators.pattern('^[0-9]+$')]],
        hallExplain: [this.hall!.hall_explain],
        hallName: [this.hall!.hall_names, [Validators.required]],
        hallLat: [this.hall!.latitude, [Validators.required, Validators.min(-90), Validators.max(90), Validators.pattern('^(\-)?([1-9][0-9]{0,1}|0)(\.[0-9]{1,8})?$')]],
        hallLng: [this.hall!.longitude, [Validators.required, Validators.min(-180), Validators.max(180), Validators.pattern('^(\-)?([1-9][0-9]{0,2}|0)(\.[0-9]{1,8})?$')]],
        showMarker: [this.hall!.show_marker, []],
        menuDispType: [this.hall!.menu_disp_type, [Validators.required]],
        menuDispMapRange: [this.hall!.menu_disp_map_range ? this.hall!.menu_disp_map_range : AppConst.MENU_DISP_MAP_RANGE_NONE, [Validators.required]],
        showPaidContent: [this.hall!.show_paid_content, []],
        hallDescs: [this.hall!.hall_descs, []],
        salesStartDate: [this.hall!.sales_start_date, []],
        salesEndDate: [this.hall!.sales_end_date, []],
        price: [this.hall!.price, []],
        validityPeriod: [this.hall!.validity_period, []],   
        useCustomMenuIcon: [this.hall!.use_custom_menu_icon, []],
        useCustomSideIcon: [this.hall!.use_custom_side_icon, []],
        useCustomSideImage: [this.hall!.use_custom_side_image, []],
        useCustomMenu: [
          this.hall!.use_custom_menu ? this.hall!.use_custom_menu : AppConst.USE_FUNCTION_PC_SP, [Validators.required]
        ],
      }, {
        validator: this.isValidSalesDate
      }
    );
  }

  //更新ボタン
  addHall(): void {
    if (this.hallFormGroup.valid) {
      this.hall!.hall_id = this.hallIdControl.value;
      this.hall!.hall_explain = this.hallExplainControl.value;
      this.hall!.hall_names = this.hallNameControl.value;
      this.hall!.latitude = this.hallLatControl.value;
      this.hall!.longitude = this.hallLngControl.value;
      this.hall!.show_marker = this.showMarkerControl.value;
      this.hall!.menu_disp_type = this.menuDispTypeControl.value;
      this.hall!.menu_disp_map_range = this.menuDispMapRangeControl.value;
      this.hall!.show_paid_content = this.showPaidContentControl.value;
      this.hall!.hall_descs = this.hallDescsControl.value;
      this.hall!.sales_start_date = this.salesStartDateControl.value ? this.salesStartDateControl.value : null;
      this.hall!.sales_end_date = this.salesEndDateControl.value ? this.salesEndDateControl.value : null;
      this.hall!.price = this.priceControl.value;
      this.hall!.validity_period = this.validityPeriodControl.value;
      this.hall!.use_custom_menu = this.useCustomMenuControl.value;
      this.hall!.use_custom_menu_icon = this.useCustomMenuIconControl.value;
      this.hall!.use_custom_side_icon = this.useCustomSideIconControl.value;
      this.hall!.use_custom_side_image = this.useCustomSideImageControl.value;
      if (this.hall) {
        let request: ApiSetHallsRequest = {
          access_token: '',
          change_type: this.data.change_type,
          halls: [this.hall]
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setHalls(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('登録しました。'));
              this.dialogRef.close();
            } else if (response!.ret === AppConst.API_RET_ALREADY_EXIST_DATA) {
              this.toastr.error(this.translate.instant('既に同じIDが登録済みです。'), '', { timeOut : 5000 });
            } else {
              console.error('api update error. ret='+response!.ret);
              this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
          }
        );
      }
    }
  }

  //属性名称ダイアログ
  openHallNameDialog(): void {
    const dialogRef = this.dialog.open(MultiNameDialogComponent, {
      width: '60%',
      height: '90%',
      disableClose: true,
      data: { names: this.hall!.hall_names }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.hall!.hall_names = result;
        this.hallNameControl.setValue(this.hall!.hall_names);
      }
    });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  // マップ検索用のキー制御
  onKeyDownFromSearchPlace(keyCode: number): boolean {
    if (keyCode && keyCode === 13) {
      // NOTE: Enterを叩かれると他の項目の編集ボタンが反応してしまうのを防ぐ
      return false;
    }
    return true;
  }

  // マップ検索
  searchMap(): void {
    const places = this.searchBox.getPlaces();
    if (places!.length === 0) {
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    places!.forEach(place => {
      if (!place.geometry || !place.geometry.location) {
        return;
      }
      if (place.geometry.viewport) {
        bounds.union(place.geometry.viewport);
      } else {
        bounds.extend(place.geometry.location);
      }
    });
    // console.log(bounds.getCenter().lat());
    // this.map.fitBounds(bounds);
    this.hallLatControl.setValue(this.round(bounds.getCenter().lat(), 8));
    this.hallLngControl.setValue(this.round(bounds.getCenter().lng(), 8));
    this.currentPosition = {
      lat: this.round(bounds.getCenter().lat(), 8),
      lng: this.round(bounds.getCenter().lng(), 8)
    }
    this.map.panTo(this.currentPosition);
  }

  // マップ検索フィールドクリア
  clearMapSearchField(): void {
    this.searchField.nativeElement.value = "";
  }

  // 有償コンテンツ表示フラグによる有償コンテンツ関連項目のバリデーションの切り替え
  changeShowPaidContent() {
    const descValidator = this.showPaidContentControl.value ? [Validators.required] : [];
    const numberValidator = this.showPaidContentControl.value ? [Validators.required, Validators.pattern('^[0-9]*$')] : [];
    this.hallDescsControl.setValidators(descValidator);
    this.priceControl.setValidators(numberValidator);
    this.validityPeriodControl.setValidators(numberValidator);
    this.hallDescsControl.updateValueAndValidity();
    this.priceControl.updateValueAndValidity();
    this.validityPeriodControl.updateValueAndValidity();
    if (!!this.showPaidContentControl.value) {
      this.menuDispMapRangeControl.setValue(this.menuDispMapRanges[0]);
    }
  }

  //建物詳細ダイアログ
  openHallDescDialog(): void {
    const dialogRef = this.dialog.open(MultiDescDialogComponent, {
      width: '60%',
      height: '90%',
      disableClose: true,
      data: { descs: this.hall!.hall_descs }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.hall!.hall_descs = result;
        this.hallDescsControl.setValue(this.hall!.hall_descs);
      }
    });
  }

  // 販売期間整合性チェック
  isValidSalesDate(ac: AbstractControl) {
    const startDate = ac.get('salesStartDate');
    const endDate = ac.get('salesEndDate');
    endDate?.setErrors(null);
    if (!!startDate && !!endDate && !!startDate.value && !!endDate.value) {
      if (startDate.value > endDate.value) {
        endDate.setErrors({ before_start_date: true });
      }
    }
  }

  // 販売期間(開始日)クリア
  clearSalesStartDate(): void {
    this.salesStartDateControl.setValue(null);
  }

  // 販売期間(終了)クリア
  clearSalesEndDate(): void {
    this.salesEndDateControl.setValue(null);
  }

  //建物カスタムメニューアイコン画像ファイル削除
  deleteCustomMenuIconFile(): void {
    if (!!this.hall!.custom_menu_icon_file) {
      this.hall!.custom_menu_icon_file = undefined;
      this.getCustomMenuIconFile();
    }
  }

  //建物カスタムサイドメニューアイコン画像ファイル削除
  deleteCustomSideIconFile(): void {
    if (!!this.hall!.custom_side_icon_file) {
      this.hall!.custom_side_icon_file = undefined;
      this.getCustomSideIconFile();
    }
  }

  //建物カスタムサイド画像ファイル削除
  deleteCustomSideImageFile(): void {
    if (!!this.hall!.custom_side_image_file) {
      this.hall!.custom_side_image_file = undefined;
      this.getCustomSideImageFile();
    }
  }

  //建物メニューアイコン削除
  deleteMenuIconFile(): void {
    this.hall!.menu_icon = '';
  }

  //建物サイドメニューアイコン削除
  deleteSideIconFile(): void {
    this.hall!.side_icon = '';
  }

  //建物カスタムメニューアイコン画像ファイル選択ダイアログ
  openSelectCustomMenuIconFileDialog(): void {
    const dialogRef = this.dialog.open(SelectFaviconFileDialogComponent, {
      width: '80%',
      height: '90%',
      disableClose: true,
      data: { uploadedFile: null }
    });

    dialogRef.afterClosed().subscribe(result => {
      let isChanged: boolean = false;
      if (!!result) {
        this.hall!.custom_menu_icon_file = result.id;
        isChanged = true;
      }
      if (!!isChanged) {
        this.getCustomMenuIconFile();
      }
    });
  }

  //建物カスタムサイドメニューアイコン画像ファイル選択ダイアログ
  openSelectCustomSideIconFileDialog(): void {
    const dialogRef = this.dialog.open(SelectFaviconFileDialogComponent, {
      width: '80%',
      height: '90%',
      disableClose: true,
      data: { uploadedFile: null }
    });

    dialogRef.afterClosed().subscribe(result => {
      let isChanged: boolean = false;
      if (!!result) {
        this.hall!.custom_side_icon_file = result.id;
        isChanged = true;
      }
      if (!!isChanged) {
        this.getCustomSideIconFile();
      }
    });
  }

  //建物メニューアイコン選択ダイアログ
  openSelectMenuIconDialog(): void {
    const dialogRef = this.dialog.open(SelectAngularIconDialogComponent, {
      width: '80%',
      height: '90%',
      disableClose: true,
      data: { uploadedFile: null }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.hall!.menu_icon = result.name;
      }
    });
  }

  //建物サイドメニューアイコン選択ダイアログ
  openSelectSideIconDialog(): void {
    const dialogRef = this.dialog.open(SelectAngularIconDialogComponent, {
      width: '80%',
      height: '90%',
      disableClose: true,
      data: { uploadedFile: null }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.hall!.side_icon = result.name;
      }
    });
  }

  //建物カスタムメニューアイコンファイル設定
  private getCustomMenuIconFile() {
    if (!!this.hall!.custom_menu_icon_file) {
      this.angularFirestore.doc<UploadedFile>(`${AppConst.FIRESTORE_UPLOADED_FILES}/${this.hall!.custom_menu_icon_file}`).valueChanges().subscribe(doc => {
        if (!!doc) {
          this.customMenuIconFile = doc;
        }
      });
    } else {
      this.customMenuIconFile = null as any;
    }
  }

  //建物カスタムサイドメニューアイコンファイル設定
  private getCustomSideIconFile() {
    if (!!this.hall!.custom_side_icon_file) {
      this.angularFirestore.doc<UploadedFile>(`${AppConst.FIRESTORE_UPLOADED_FILES}/${this.hall!.custom_side_icon_file}`).valueChanges().subscribe(doc => {
        if (!!doc) {
          this.customSideIconFile = doc;
        }
      });
    } else {
      this.customSideIconFile = null as any;
    }
  }

  //建物カスタムメニューアイコンファイル使用有無変更
  onChangeUseCustomMenuIcon() {
    if (!!this.useCustomMenuIconControl.value) {
      this.deleteMenuIconFile();
    } else {
      this.deleteCustomMenuIconFile();
    }
  }

  //建物カスタムサイド画像ファイル設定
  private getCustomSideImageFile() {
    if (!!this.hall!.custom_side_image_file) {
      this.angularFirestore.doc<UploadedFile>(`${AppConst.FIRESTORE_UPLOADED_FILES}/${this.hall!.custom_side_image_file}`).valueChanges().subscribe(doc => {
        if (!!doc) {
          this.customSideImageFile = doc;
        }
      });
    } else {
      this.customSideImageFile = null as any;
    }

  }

  //建物カスタムサイドメニューアイコンファイル使用有無変更
  onChangeUseCustomSideIcon() {
    if (!!this.useCustomSideIconControl.value) {
      this.deleteSideIconFile();
    } else {
      this.deleteCustomSideIconFile();
    }
  }

  //建物カスタムサイド画像ファイル使用有無変更
  onChangeUseCustomSideImage() {
    if (!!this.useCustomSideImageControl.value) {
      // 通常のサイドアイコン削除処理があれば追加
    } else {
      this.deleteCustomSideImageFile();
    }
  }

  //建物カスタムサイド画像ファイル選択ダイアログ
  openSelectCustomSideImageFileDialog(): void {
    const dialogRef = this.dialog.open(SelectFaviconFileDialogComponent, {
      width: '80%',
      height: '90%',
      disableClose: true,
      data: { uploadedFile: null }
    });

    dialogRef.afterClosed().subscribe(result => {
      let isChanged: boolean = false;
      if (!!result) {
        this.hall!.custom_side_image_file = result.id;
        isChanged = true;
      }
      if (!!isChanged) {
        this.getCustomSideImageFile();
      }
    });
  }
}